import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Web3Provider, useWeb3 } from './context/Web3Context';
import { ToastProvider } from './components/Toast';
import Layout from './components/Layout';
import Register from './pages/Register';
import Home from './pages/Home';
import Market from './pages/Market';
import MyNFTs from './pages/MyNFTs';
import Team from './pages/Team';
import Admin from './pages/Admin';
import Loader from './components/Loader';
import BlacklistNotice from './components/BlacklistNotice';
import { isAdminAddress } from './config/admin';

// Store referral on app load
const storeReferral = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const ref = urlParams.get('ref');
  if (ref) {
    sessionStorage.setItem('referralAddress', ref);
    console.log('Stored referral in session:', ref);
  }
};

// Call on initial load
storeReferral();

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { account, isRegistered, isLoading } = useWeb3();
  const location = useLocation();

  useEffect(() => {
    // Store ref param if present
    const urlParams = new URLSearchParams(location.search);
    const ref = urlParams.get('ref');
    if (ref) {
      sessionStorage.setItem('referralAddress', ref);
      console.log('ProtectedRoute - Stored referral:', ref);
    }
  }, [location.search]);

  if (isLoading) {
    return <Loader text="Loading" fullScreen />;
  }

  if (!account || !isRegistered) {
    // Preserve ref param in redirect
    const urlParams = new URLSearchParams(location.search);
    const ref = urlParams.get('ref') || sessionStorage.getItem('referralAddress');
    const redirectUrl = ref ? `/register?ref=${ref}` : '/register';
    return <Navigate to={redirectUrl} replace />;
  }

  return <Layout>{children}</Layout>;
};

// Admin Route Component — gated by connected wallet
const AdminRoute = ({ children }) => {
  const { account, isLoading } = useWeb3();

  if (isLoading) {
    return <Loader text="Loading" fullScreen />;
  }

  if (!account || !isAdminAddress(account)) {
    return <Navigate to="/" replace />;
  }

  return <Layout>{children}</Layout>;
};

// Register Route Component
const RegisterRoute = () => {
  const { account, isRegistered, isLoading } = useWeb3();
  const location = useLocation();

  useEffect(() => {
    // Store ref param if present
    const urlParams = new URLSearchParams(location.search);
    const ref = urlParams.get('ref');
    if (ref) {
      sessionStorage.setItem('referralAddress', ref);
      console.log('RegisterRoute - Stored referral:', ref);
    }
  }, [location.search]);

  if (isLoading) {
    return <Loader text="Loading" fullScreen />;
  }

  if (account && isRegistered) {
    return <Navigate to="/" replace />;
  }

  return <Register />;
};

// Main App Router
const AppRouter = () => {
  return (
    <BrowserRouter
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true
      }}
    >
      <Routes>
        <Route path="/register" element={<RegisterRoute />} />
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <Home />
            </ProtectedRoute>
          }
        />
        <Route
          path="/market"
          element={
            <ProtectedRoute>
              <Market />
            </ProtectedRoute>
          }
        />
        <Route
          path="/my-nfts"
          element={
            <ProtectedRoute>
              <MyNFTs />
            </ProtectedRoute>
          }
        />
        <Route
          path="/team"
          element={
            <ProtectedRoute>
              <Team />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin"
          element={
            <AdminRoute>
              <Admin />
            </AdminRoute>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
};

function App() {
  return (
    <Web3Provider>
      <ToastProvider>
        <AppRouter />
        <BlacklistNotice />
      </ToastProvider>
    </Web3Provider>
  );
}

export default App;
